import type { RecordingPhase } from "@cuekards/core/recording";
import { canTogglePause, stopIsCancel } from "@cuekards/core/recording";

/** The one button that both starts a take and holds it, described for the phase
 * it is in: `start` while there is nothing rolling, `togglePause` after. */
export interface RecordButton {
  label: string;
  icon: "record" | "pause";
  action: "start" | "togglePause";
  disabled: boolean;
}

/** The stop, which is a cancel during the countdown and dead when there is
 * nothing to stop. It makes one call whatever it is called. */
export interface StopButton {
  label: string;
  disabled: boolean;
}

/** The transport's row, as data. */
export interface TransportButtons {
  record: RecordButton;
  stop: StopButton;
}

/**
 * What each button in the row does right now.
 *
 * Separated from the markup because the row has no words in it any more: the
 * labels live in `aria-label` and `title`, where being wrong looks like nothing
 * at all. See `test/transport-buttons.test.ts`.
 *
 * A file of its own rather than the top of `Transport.tsx`, where it lived until
 * mobile-app ticket 01, and the reason is the same one it was separated from the
 * markup for, one step further out: this is the row's *decision*, and a third
 * host that draws no `<button>` still has to make it the same way. A module that
 * a native host can read is a module with no JSX in it, which is what
 * `test/hooks-are-dom-free.test.ts` holds. `Transport.tsx` and
 * `@cuekards/ui`'s root both still hand it out, so nothing that imported it
 * before imports it from anywhere new.
 */
export function transportButtons(phase: RecordingPhase): TransportButtons {
  const idle = phase === "idle";
  // The icon says what pressing it would do rather than what is happening: the
  // square rolls, the bars hold. That is what lets one button cover three
  // meanings without a fourth icon for resume.
  const rolls = idle || phase === "paused";
  return {
    record: {
      label: idle ? "Record" : phase === "paused" ? "Resume" : "Pause",
      icon: rolls ? "record" : "pause",
      action: idle ? "start" : "togglePause",
      disabled: idle ? false : !canTogglePause(phase),
    },
    stop: {
      label: stopIsCancel(phase) ? "Cancel" : "Stop",
      disabled: idle || phase === "stopping",
    },
  };
}
